import { useEffect, useMemo, useState } from "react";
import { api, formatINR, describeAxiosError, downloadXlsx } from "@/lib/api";
import { Loader2, Plus, X, FileSpreadsheet } from "lucide-react";
import { IncrementRecords } from "@/components/increments/IncrementRecords";
import { RecalculateIncrementDialog } from "@/components/increments/RecalculateIncrementDialog";

export default function Increments() {
  const [tab, setTab] = useState("employees");   // "employees" | "workers"
  const [empIncs, setEmpIncs] = useState([]);
  const [wrkIncs, setWrkIncs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [recalculating, setRecalculating] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [e, w] = await Promise.all([
      api.get("/increments"),
      api.get("/worker-increments").catch(() => ({ data: [] })),
    ]);
    setEmpIncs(e.data || []);
    setWrkIncs(w.data || []);
    } catch (e) { setError(describeAxiosError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const list = tab === "employees" ? empIncs : wrkIncs;

  return (
    <div className="space-y-6" data-testid="increments-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Career Growth</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1" data-testid="increments-title">Increment History</h1>
          <p className="text-sm text-stone-600 mt-1" data-testid="increment-effective-notice">
            Employee salary changes apply from the selected increment month. Earlier payroll months remain unchanged.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => downloadXlsx("/increments/export.xlsx", "increments_register.xlsx")}
            className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium"
            style={{ background: "#1B4332" }} data-testid="export-increments-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-increment-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium active:scale-95 transition"
                  style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> Add Increment
          </button>
        </div>
      </header>

      <div className="flex items-center gap-1 border-b border-stone-200">
        {[
          ["employees", "Employees", empIncs.length],
          ["workers", "Workers / Labourers", wrkIncs.length],
        ].map(([k, label, count]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`inc-tab-${k}`}
                  className="px-4 py-2 text-sm font-medium border-b-2 -mb-px transition"
                  style={tab === k
                    ? { borderColor: "var(--color-primary)", color: "var(--color-primary)" }
                    : { borderColor: "transparent", color: "#78716c" }}>
            {label} <span className="ml-1 text-xs text-stone-400">({count})</span>
          </button>
        ))}
      </div>

      {error && <p role="alert" className="text-sm text-red-700" data-testid="increments-error">{error}<button onClick={load} className="ml-2 underline" data-testid="increments-retry">Retry</button></p>}
      {notice && <p role="status" className="text-sm text-primary" data-testid="increments-save-notice">{notice}</p>}
      <div className="card-flat p-0 min-w-0">
        {loading ? (
          <div className="flex items-center justify-center py-24" data-testid="increments-loading"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : <IncrementRecords items={list} workers={tab === "workers"} onRecalculate={setRecalculating} />}
      </div>

      {showAdd && (
        <AddIncrementModal tab={tab} onClose={() => setShowAdd(false)}
                            onSaved={(result) => { setShowAdd(false); setNotice(result?.payroll_updated != null ? `Increment saved. ${result.payroll_updated} payroll row(s) updated; earlier months unchanged.` : "Increment saved."); load(); }} />
      )}
      {recalculating && <RecalculateIncrementDialog record={recalculating} onClose={() => setRecalculating(null)} onSaved={(result) => { setRecalculating(null); setNotice(`${result.payroll_updated} payroll row(s) recalculated. Earlier months unchanged.`); load(); }} />}
    </div>
  );
}

function AddIncrementModal({ tab, onClose, onSaved }) {
  const [mode, setMode] = useState(tab);   // "employees" | "workers"
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({
    person_id: "",
    effective_date: new Date().toISOString().slice(0, 10),
    effective_month: new Date().toISOString().slice(0, 7),
    new_ctc: 0,
    new_daily_rate: 0,
    new_hourly_rate: 0,
    remarks: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const url = mode === "employees" ? "/employees?status=active" : "/workers";
    api.get(url).then((r) => setPeople(r.data || [])).catch((e) => { setPeople([]); setErr(describeAxiosError(e)); });
    setForm((f) => ({ ...f, person_id: "" }));
  }, [mode]);

  const selected = useMemo(() =>
    people.find((p) => p.id === form.person_id), [people, form.person_id]);

  const prevCTC = mode === "employees"
    ? Number(selected?.ctc_annual || 0)
    : Number(selected?.ctc_annual || 0);
  const prevDaily = Number(selected?.daily_rate || 0);
  const isContract = mode === "workers" && selected?.worker_type === "contract";

  const submit = async (ev) => {
    ev.preventDefault(); if (busy) return; setBusy(true); setErr("");
    try {
      let result;
      if (mode === "employees") {
        result = await api.post("/increments", {
          employee_id: form.person_id,
          effective_month: form.effective_month,
          new_ctc: Number(form.new_ctc),
          remarks: form.remarks,
        });
      } else {
        result = await api.post("/worker-increments", {
          worker_id: form.person_id,
          effective_date: form.effective_date,
          new_ctc: isContract ? 0 : Number(form.new_ctc),
          new_daily_rate: isContract ? Number(form.new_daily_rate) : 0,
          new_hourly_rate: Number(form.new_hourly_rate) || 0,
          remarks: form.remarks,
        });
      }
      onSaved(result.data);
    } catch (e) {
      setErr(describeAxiosError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="add-increment-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto p-6" role="dialog" aria-modal="true" aria-labelledby="add-increment-title">
        <div className="flex items-center justify-between mb-4">
          <h2 id="add-increment-title" className="font-heading text-xl font-bold" data-testid="add-increment-title">Add Increment</h2>
          <button onClick={onClose} disabled={busy} aria-label="Close increment form" data-testid="increment-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-1 mb-4 bg-stone-100 rounded-md p-1 w-fit">
          {[["employees", "Employee"], ["workers", "Worker / Labourer"]].map(([k, label]) => (
            <button key={k} type="button" disabled={busy} onClick={() => setMode(k)}
                    data-testid={`inc-mode-${k}`}
                    className="px-3 py-1.5 text-xs font-medium rounded transition"
                    style={mode === k
                      ? { background: "white", color: "var(--color-primary)", boxShadow: "0 1px 2px rgba(0,0,0,.06)" }
                      : { color: "#78716c" }}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="increment-form">
          <fieldset disabled={busy} className="space-y-3" data-testid="increment-fields">
          <label className="block">
            <div className="kpi-label mb-1.5">{mode === "employees" ? "Employee" : "Worker"}</div>
            <select required value={form.person_id}
                    onChange={(e) => setForm({ ...form, person_id: e.target.value })}
                    data-testid="inc-person-select"
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
              <option value="">— Select —</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {`${p.full_name || p.name} ${p.employee_code || p.worker_code ? `· ${p.employee_code || p.worker_code}` : ""}`}
                </option>
              ))}
            </select>
          </label>

          {selected && (
            <div className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-md px-3 py-2" data-testid="increment-current-salary">
              Current: {isContract
                ? <>Daily <b>₹{prevDaily}</b></>
                : <>CTC <b>{formatINR(prevCTC)}</b>{selected.hourly_rate ? <> · Hourly <b>₹{selected.hourly_rate}</b></> : null}</>}
            </div>
          )}

          <label className="block">
            <div className="kpi-label mb-1.5">{mode === "employees" ? "Increment Month" : "Effective Date"}</div>
            <input type={mode === "employees" ? "month" : "date"} required value={mode === "employees" ? form.effective_month : form.effective_date}
                   onChange={(e) => setForm({ ...form, [mode === "employees" ? "effective_month" : "effective_date"]: e.target.value })}
                   data-testid={mode === "employees" ? "inc-effective-month" : "inc-effective-date"}
                   className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm" />
          </label>

          {isContract ? (
            <label className="block">
              <div className="kpi-label mb-1.5">New Daily Rate ₹</div>
              <input type="number" required value={form.new_daily_rate}
                     onChange={(e) => setForm({ ...form, new_daily_rate: e.target.value })}
                     data-testid="inc-new-daily-rate" placeholder={String(prevDaily)}
                     className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm" />
            </label>
          ) : (
            <label className="block">
              <div className="kpi-label mb-1.5">{mode === "employees" ? "New Annual Gross / CTC ₹" : "New CTC (Annual ₹)"}</div>
              <input type="number" min="1" step="1" required value={form.new_ctc}
                     onChange={(e) => setForm({ ...form, new_ctc: e.target.value })}
                     data-testid="inc-new-ctc" placeholder={String(prevCTC)}
                     className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm" />
            </label>
          )}

          {mode === "employees" && Number(form.new_ctc) > 0 && <div className="border-y border-stone-200 py-3 text-sm" data-testid="increment-salary-preview"><span>New monthly gross</span><strong className="block text-xl text-primary mt-1" data-testid="increment-new-monthly-gross">{formatINR(Math.round(Number(form.new_ctc) / 12))}</strong><p className="text-xs text-stone-500 mt-1" data-testid="increment-salary-basis">Basic 50% · HRA 20% · Conveyance ₹800 · Medical ₹1,250 · Special: remainder</p></div>}

          {mode === "workers" && (
            <label className="block">
              <div className="kpi-label mb-1.5">New Hourly Rate ₹/hr <span className="text-stone-400 font-normal">(optional)</span></div>
              <input type="number" value={form.new_hourly_rate}
                     onChange={(e) => setForm({ ...form, new_hourly_rate: e.target.value })}
                     data-testid="inc-new-hourly-rate"
                     className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm" />
            </label>
          )}

          <label className="block">
            <div className="kpi-label mb-1.5">Remarks</div>
            <textarea rows="2" value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      placeholder="e.g. Annual appraisal 2026, Promotion to Sr. …"
                      data-testid="inc-remarks"
                      className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm" />
          </label>

          </fieldset>
          {err && <div role="alert" className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-md" data-testid="increment-form-error">{err}</div>}

          <button type="submit" disabled={busy || !form.person_id}
                  data-testid="save-increment-button"
                  className="w-full py-2 rounded-md text-white font-semibold disabled:opacity-50"
                  style={{ background: "var(--color-primary)" }}>
            {busy ? "Saving…" : "Save Increment"}
          </button>
        </form>
      </div>
    </div>
  );
}
